import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { FadeUp, MaskedLines, GalleryRow } from '../components/anim';
import Lightbox from '../components/Lightbox';
import { CHAPTERS, UI } from '../data/portfolio';
import { useLang } from '../i18n';
import { useProtectedProjects } from '../lib/protected';

function RetailChapter({ chapter }) {
  const { t } = useLang();
  const [lb, setLb] = useState(null);
  return (
    <>
      {/* Global roll-out map */}
      <FadeUp className="mt-20 md:mt-28">
        <figure>
          <div className="overflow-hidden bg-paper">
            <img
              src={`/work/${chapter.rollout.image}.jpg`}
              alt={t(chapter.rollout.title)}
              data-testid="retail-world-map"
              className="w-full object-cover"
            />
          </div>
          <figcaption className="mt-3 text-[10px] uppercase tracking-[0.25em] text-ink/50">
            {t(chapter.rollout.title)} — {t(chapter.rollout.subtitle)}
          </figcaption>
        </figure>
      </FadeUp>

      {/* Regions — mosaic: 1 full-width photo + 2 side by side, with titles */}
      <div className="mt-24 space-y-28 md:mt-32 md:space-y-40">
        {chapter.regions.map((region, ri) => {
          const rid = region.name.en.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return (
          <section key={region.name.en} data-testid={`region-${rid}`}>
            <FadeUp>
              <h2 className="text-2xl font-light uppercase tracking-[0.12em] md:text-4xl">{t(region.name)}</h2>
              <div className="mt-6 h-px bg-ink/25" />
            </FadeUp>
            <FadeUp delay={0.08} className="mt-12">
              <figure>
                <div
                  className="group cursor-zoom-in overflow-hidden bg-paper aspect-[16/9]"
                  onClick={() => setLb({ ri, pi: 0 })}
                  data-testid={`region-photo-open-${rid}-0`}
                >
                  <img
                    src={`/work/${region.projects[0].photo}.jpg`}
                    alt={region.projects[0].name}
                    data-testid={`region-photo-${rid}-0`}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                  />
                </div>
                <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/50">
                  <span className="text-ink/70">{region.projects[0].name}</span>
                  <span>{t(region.projects[0].spec)}</span>
                </figcaption>
              </figure>
            </FadeUp>
            <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6">
              {region.projects.slice(1).map((p, pi) => (
                <FadeUp key={p.name + pi} delay={0.12 + pi * 0.06}>
                  <figure>
                    <div
                      className="group cursor-zoom-in overflow-hidden bg-paper aspect-[4/3]"
                      onClick={() => setLb({ ri, pi: pi + 1 })}
                      data-testid={`region-photo-open-${rid}-${pi + 1}`}
                    >
                      <img
                        src={`/work/${p.photo}.jpg`}
                        alt={p.name}
                        data-testid={`region-photo-${rid}-${pi + 1}`}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                      />
                    </div>
                    <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/50">
                      <span className="text-ink/70">{p.name}</span>
                      <span>{t(p.spec)}</span>
                    </figcaption>
                  </figure>
                </FadeUp>
              ))}
            </div>
          </section>
          );
        })}
      </div>
      <AnimatePresence>
        {lb != null && (
          <Lightbox
            photos={chapter.regions[lb.ri].projects.map((p) => ({ s: p.photo }))}
            index={lb.pi}
            onClose={() => setLb(null)}
            onNav={(d) => setLb((v) => ({ ...v, pi: (v.pi + d + chapter.regions[v.ri].projects.length) % chapter.regions[v.ri].projects.length }))}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default function Chapter() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const protectedProjects = useProtectedProjects();
  const idx = CHAPTERS.findIndex((c) => c.slug === slug);
  if (idx === -1) return <Navigate to="/work" replace />;
  const chapter = CHAPTERS[idx];
  const prev = CHAPTERS[(idx + CHAPTERS.length - 1) % CHAPTERS.length];
  const next = CHAPTERS[(idx + 1) % CHAPTERS.length];

  return (
    <main data-testid={`chapter-page-${slug}`} className="px-6 pt-28 md:px-10 md:pt-40">
      <div className="mx-auto max-w-[1600px]">
        <FadeUp>
          <div className="text-[10px] uppercase tracking-[0.3em] text-ink/50 md:text-xs">
            {t(UI.chapterLabel)} {chapter.numeral}
          </div>
        </FadeUp>
        <MaskedLines
          className="mt-8"
          delay={0.15}
          lines={[
            <h1
              key="t"
              data-testid={`chapter-heading-${slug}`}
              className="text-[clamp(28px,4.3vw,58px)] font-light uppercase leading-tight tracking-[0.08em] text-balance"
            >
              {chapter.title}
            </h1>,
          ]}
        />
        <div className="mt-10 h-px bg-ink/25" />
        <FadeUp delay={0.2}>
          <p className="mt-10 max-w-[36rem] text-[15px] leading-[1.55] text-ink/85 text-pretty md:text-[16px]">
            {t(chapter.intro)}
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <p className="mt-6 text-xs uppercase leading-relaxed tracking-[0.25em] text-ink">
            {t(chapter.expertiseLine)}
          </p>
        </FadeUp>

        {chapter.quote && (
          <FadeUp delay={0.1}>
            <div className="mt-20 border-y border-ink/10 py-16 text-center md:mt-28 md:py-24">
              <p className="mx-auto max-w-3xl text-xl italic leading-snug md:text-3xl" style={{ fontFamily: "'Gotham', 'Jost', sans-serif", fontStyle: 'italic', fontWeight: 300 }}>
                {t(chapter.quote)}
              </p>
              <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-ink/40">{chapter.quote.author}</p>
            </div>
          </FadeUp>
        )}

        {chapter.regions ? (
          <RetailChapter chapter={chapter} />
        ) : (
          <div className="mt-20 space-y-24 md:mt-28 md:space-y-36">
            {chapter.projects.map((p) => {
              const locked = protectedProjects.includes(p.slug);
              return (
              <section key={p.slug} data-testid={`project-${p.slug}`}>
                <div className="grid gap-6 md:grid-cols-12 md:items-end">
                  <div className="md:col-span-7">
                    <FadeUp>
                      <h2 className="text-[clamp(20px,3.2vw,37px)] font-light uppercase tracking-[0.1em] text-balance">
                        <Link
                          to={`/work/${slug}/${p.slug}`}
                          data-testid={`project-link-${p.slug}`}
                          className="transition-colors duration-300 hover:text-ink/60"
                        >
                          {p.title}
                          {protectedProjects.includes(p.slug) && (
                            <Lock
                              size={13}
                              strokeWidth={1.5}
                              aria-label="Protected project"
                              data-testid={`lock-${p.slug}`}
                              className="ml-3 inline-block align-baseline text-ink/40"
                            />
                          )}
                        </Link>
                      </h2>
                    </FadeUp>
                    {p.caption && (
                      <FadeUp delay={0.06}>
                        <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-ink/60">{t(p.caption)}</p>
                      </FadeUp>
                    )}
                  </div>
                  <div className="md:col-span-5">
                    <FadeUp delay={0.08}>
                      {p.credits && (
                        <div className="space-y-1 md:text-right">
                          {t(p.credits).split(' — ').map((line, i) => (
                            <p key={i} className="text-[10px] uppercase tracking-[0.25em] text-ink/40">{line}</p>
                          ))}
                        </div>
                      )}
                    </FadeUp>
                  </div>
                </div>
                {!locked && p.texts && (
                  <FadeUp delay={0.12}>
                    <div className="mt-6 max-w-[36rem] space-y-4">
                      {p.texts.map((tx, i) => (
                        <p key={i} className="text-[15px] leading-[1.55] text-ink/80 text-pretty md:text-[16px]">
                          {t(tx)}
                        </p>
                      ))}
                    </div>
                  </FadeUp>
                )}
                {!locked && (
                  <div className="mt-10 space-y-4 md:space-y-6">
                    {p.gallery.map((row, ri) => (
                      <GalleryRow key={ri} row={row} />
                    ))}
                  </div>
                )}
                <div className="mt-16 h-px bg-ink/10" />
              </section>
              );
            })}
          </div>
        )}

        {/* Prev / next */}
        <div className="grid gap-4 border-t border-ink/15 py-12 md:grid-cols-2 md:py-16">
          <Link to={`/work/${prev.slug}`} data-testid={`chapter-prev-${prev.slug}`} className="group">
            <div className="text-[10px] uppercase tracking-[0.3em] text-ink/40">{t(UI.previous)}</div>
            <div className="mt-2 text-lg font-light uppercase tracking-[0.12em] transition-transform duration-500 group-hover:-translate-x-2 md:text-3xl">
              ← {prev.title}
            </div>
          </Link>
          <Link to={`/work/${next.slug}`} data-testid={`chapter-next-${next.slug}`} className="group md:text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-ink/40">{t(UI.next)}</div>
            <div className="mt-2 text-lg font-light uppercase tracking-[0.12em] transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
              {next.title} →
            </div>
          </Link>
        </div>
      </div>
    </main>
  );
}
